import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Checkbox, FormControlLabel, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import WarningIcon from '@mui/icons-material/Warning';
import { useMeContext } from '~/modules/me/useMeContext';
import {
  objectToFormatString,
  getLuxonJsDateFormatFromMe
} from '~/modules/common/dates/convert';
import Decimal from '~/modules/common/components/Decimal';
import { FieldLabel } from '~/modules/tasks/components/TaskDrawer/TaskResourceEstimates/TaskAllocationEditor';

const useStyles = makeStyles(theme => ({
  warning: {
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    columnGap: theme.spacing(1)
  },
  warningIcon: {
    color: theme.palette.text.secondary,
    fontSize: '16px !important'
  },
  hours: {
    ...theme.typography.body1
  }
}));

const getWarningMessage = ({
  startDate,
  endDate,
  estimatedHours,
  resource
}) => {
  if (!startDate || !endDate)
    return 'taskResourceAssignments.allocateResourceTime.warnings.taskDatesRequired';

  if (!estimatedHours)
    return 'taskResourceAssignments.allocateResourceTime.warnings.estimatedHoursRequired';

  if (!resource)
    return 'taskResourceAssignments.allocateResourceTime.warnings.resourceRequired';

  return null;
};

export const AllocateResourceTimeField = ({
  startDate,
  endDate,
  estimatedHours,
  resource,
  rowId,
  isCreateAllocationEnabled,
  onAllocateResourceTimeChange
}) => {
  const classes = useStyles();
  const me = useMeContext();
  const { formatMessage } = useIntl();
  const dateFormatString = getLuxonJsDateFormatFromMe(me);

  const dateRangeValues = useMemo(
    () => ({
      startDate: objectToFormatString(startDate, dateFormatString),
      endDate: objectToFormatString(endDate, dateFormatString)
    }),
    [dateFormatString, endDate, startDate]
  );

  const hasWarning = getWarningMessage({
    startDate,
    endDate,
    estimatedHours,
    resource
  });

  const checkboxLabelId = useMemo(
    () =>
      hasWarning ? `${rowId}-allocation-warning` : `${rowId}-allocation-info`,
    [hasWarning, rowId]
  );

  const inputProps = useMemo(
    () => ({
      'aria-label': formatMessage({
        id: 'taskResourceAssignments.allocateResourceTime.label'
      }),
      'aria-describedby': checkboxLabelId
    }),
    [formatMessage, checkboxLabelId]
  );

  return (
    <FormControlLabel
      control={
        <Checkbox
          checked={isCreateAllocationEnabled}
          data-testid="AllocateResourceTimeCheckbox"
          onChange={onAllocateResourceTimeChange}
          inputProps={inputProps}
        />
      }
      label={
        hasWarning ? (
          <Typography
            id={checkboxLabelId}
            className={classes.warning}
            variant="caption"
          >
            <WarningIcon className={classes.warningIcon} />
            <FormattedMessage id={hasWarning} />
          </Typography>
        ) : (
          <div id={checkboxLabelId}>
            <FieldLabel
              variant="body1"
              messageId="taskResourceAssignments.allocateResourceTime.message"
              messageValues={{
                value: (
                  <Decimal value={estimatedHours} className={classes.hours} />
                )
              }}
            />
            <FieldLabel
              messageId="taskResourceEstimates.dateRange"
              messageValues={dateRangeValues}
              color="textSecondary"
            />
          </div>
        )
      }
    />
  );
};

AllocateResourceTimeField.propTypes = {
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  estimatedHours: PropTypes.number,
  resource: PropTypes.object,
  rowId: PropTypes.string.isRequired,
  isCreateAllocationEnabled: PropTypes.bool,
  onAllocateResourceTimeChange: PropTypes.func.isRequired
};

export default AllocateResourceTimeField;
